import { yupResolver } from "@hookform/resolvers/yup";
import {
  Autocomplete,
  CircularProgress,
  Grid,
  TextField,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";
import FormWrapper from "src/components/FormWrapper";
import { FormProvider, RHFTextField } from "src/components/hook-form";
import { saveDesignation, getActiveCompanies } from "src/pages/api";
import useAuth from "src/hooks/useAuth";
import * as Yup from "yup";

export default function AddDesignation({ onClose, row, isEditMode, defaultCompany, onSuccess }) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const isCompanyUser = user?.role === "COMPANY";

  const defaultValues = {
    name: "",
    company: null,
  };

  const schema = Yup.object().shape({
    name: Yup.string().trim().required("Designation name is required"),
    ...(!isCompanyUser && {
      company: Yup.object().nullable().required("Company is required"),
    }),
  });

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const { handleSubmit, reset, setValue, control } = methods;

  const { data: companyOptions = [], isFetching: companiesLoading } = useQuery(
    ["companies-active-designation"],
    ({ signal }) => getActiveCompanies({ signal }),
    { enabled: !isCompanyUser },
  );

  useEffect(() => {
    if (isEditMode && row) {
      setValue("name", row.name || "");
      if (!isCompanyUser && row.companyId) {
        setValue("company", {
          id: row.companyId,
          name: row.company?.name || row.companyName || "",
        });
      }
    } else if (!isEditMode && !isCompanyUser && defaultCompany?.id) {
      setValue("company", {
        id: defaultCompany.id,
        name: defaultCompany.name || "",
      });
    }
  }, [isEditMode, row, setValue, isCompanyUser, defaultCompany]);

  const mutation = useMutation(
    (formData) =>
      saveDesignation({ id: isEditMode ? row.id : undefined, req: formData }),
    {
      onSuccess: (response) => {
        enqueueSnackbar(
          isEditMode ? "Designation updated successfully" : "Designation created successfully",
          { variant: "success" }
        );
        queryClient.invalidateQueries(["designations"]);
        onClose();
        if (typeof onSuccess === "function") {
          onSuccess(response);
        }
      },
      onError: (error) => {
        const apiMessage =
          error?.response?.data?.details ||
          error?.response?.data?.error ||
          error?.response?.data?.message;
        enqueueSnackbar(apiMessage || error?.message || "Failed to save designation", {
          variant: "error",
        });
      },
    }
  );

  const onSubmit = (data) => {
    const payload = { name: data.name };
    if (!isCompanyUser && data.company) {
      payload.companyId = String(data.company.id);
    }
    mutation.mutate(payload);
  };

  return (
    <FormWrapper
      title={isEditMode ? "Edit Designation" : "Add Designation"}
      onClose={onClose}
      onSubmit={handleSubmit(onSubmit)}
      onReset={() => reset(defaultValues)}
      loading={mutation.isLoading}
      isEditMode={isEditMode}
    >
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {!isCompanyUser && (
            <Grid item xs={12}>
              <Controller
                name="company"
                control={control}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    options={companyOptions}
                    value={field.value}
                    onChange={(_, val) => field.onChange(val)}
                    getOptionLabel={(opt) => opt?.name || ""}
                    isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                    loading={companiesLoading}
                    noOptionsText="No companies found"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        label="Company"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {companiesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                )}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <RHFTextField name="name" required label="Designation Name" autoFocus />
          </Grid>
        </Grid>
      </FormProvider>
    </FormWrapper>
  );
}
